import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import type {
  HcpDetailsResponse, HcpPreviewResponse, HcpAlignmentResponse,
  HcoDetailsResponse, HcoAlignmentResponse, HcoPreviewRow,
  ChangeRequestRow2, HcpAffiliationsPreviewRow, CtmsAddressRow2,
} from '../types/api/generated';
import {
  HcpDetailsResponseSchema, HcpPreviewResponseSchema, HcpAlignmentResponseSchema,
  HcoDetailsResponseSchema, HcoAlignmentResponseSchema,
} from '../types/api/schemas';
import { validateResponse } from './validateResponse';
import { z } from 'zod';

export const detailsApi = createApi({
    reducerPath: 'detailsApi',
    baseQuery,
    endpoints: (builder) => ({
        getHcpDetails: builder.mutation<HcpDetailsResponse, unknown>({
            query: (body) => ({ url: 'api/v2/details/hcp', method: 'POST', body }),
            transformResponse: validateResponse(HcpDetailsResponseSchema),
        }),
        getHcpChangeRequests: builder.mutation<ChangeRequestRow2[], unknown>({
            query: (body) => ({ url: 'api/v2/details/hcp-change-requests', method: 'POST', body }),
        }),
        getHcpPreview: builder.mutation<HcpPreviewResponse, unknown>({
            query: (body) => ({ url: 'api/v2/details/hcp-preview', method: 'POST', body }),
            transformResponse: validateResponse(HcpPreviewResponseSchema),
        }),
        getHcpAffiliationsPreview: builder.mutation<HcpAffiliationsPreviewRow[], unknown>({
            query: (body) => ({ url: 'api/v2/details/hcp-affiliations-preview', method: 'POST', body }),
        }),
        getHcpPreviewCtms: builder.mutation<CtmsAddressRow2[], unknown>({
            query: (body) => ({ url: 'api/v2/details/hcp-preview-ctms', method: 'POST', body }),
        }),
        getHcpAlignment: builder.mutation<HcpAlignmentResponse, unknown>({
            query: (body) => ({ url: 'api/v2/details/hcp-alignment', method: 'POST', body }),
            transformResponse: validateResponse(HcpAlignmentResponseSchema),
        }),
        getHcoDetails: builder.mutation<HcoDetailsResponse, unknown>({
            query: (body) => ({ url: 'api/v2/details/hco', method: 'POST', body }),
            transformResponse: validateResponse(HcoDetailsResponseSchema),
        }),
        getHcoChangeRequests: builder.mutation<ChangeRequestRow2[], unknown>({
            query: (body) => ({ url: 'api/v2/details/hco-change-requests', method: 'POST', body }),
        }),
        getHcoPreview: builder.mutation<HcoPreviewRow[], unknown>({
            query: (body) => ({ url: 'api/v2/details/hco-preview', method: 'POST', body }),
        }),
        getHcoAlignment: builder.mutation<HcoAlignmentResponse, unknown>({
            query: (body) => ({ url: 'api/v2/details/hco-alignment', method: 'POST', body }),
            transformResponse: validateResponse(HcoAlignmentResponseSchema),
        }),
        getAffiliationsOrganizations: builder.mutation<unknown[], unknown>({
            query: (body) => ({ url: 'api/v2/details/affiliations-organizations', method: 'POST', body }),
        }),
        getAffiliationsProfessionals: builder.mutation<unknown[], unknown>({
            query: (body) => ({ url: 'api/v2/details/affiliations-professionals', method: 'POST', body }),
        }),
    }),
});

export const {
    useGetHcpDetailsMutation,
    useGetHcpChangeRequestsMutation,
    useGetHcpPreviewMutation,
    useGetHcpAffiliationsPreviewMutation,
    useGetHcpPreviewCtmsMutation,
    useGetHcpAlignmentMutation,
    useGetHcoDetailsMutation,
    useGetHcoChangeRequestsMutation,
    useGetHcoPreviewMutation,
    useGetHcoAlignmentMutation,
    useGetAffiliationsOrganizationsMutation,
    useGetAffiliationsProfessionalsMutation,
} = detailsApi;
